import {isRouteErrorResponse, Navigate, useRouteError} from "react-router-dom";
import {Box, Heading, Text, Flex, ButtonGroup, Icon, Link} from "@chakra-ui/react";
import {Button} from "./ui/button";
import Loading from "./Loading";
import {HTTPError} from "platformed-client";
import {RedirectError} from "../api";
import {ReactNode, useCallback, useEffect, useRef, PropsWithChildren} from "react";
import T from "platformed-components/i18n/T";
import {ErrorBoundary, FallbackRender} from "@sentry/react";
import {router} from "../router";
import {ExclamationTriangleIcon} from "@heroicons/react/20/solid";
import {Internal} from "./InternalMode";

type ErrorResponse = {status: number; data?: any};

const ERROR_MESSAGES: {[status: number]: {title: ReactNode; description: ReactNode} | undefined} = {
  404: {
    title: <T keyName="c.Page_not_found" />,
    description: <T keyName="error_boundary.page_not_found_description" />,
  },
  403: {title: <T keyName="c.Unauthorized" />, description: <T keyName="error_boundary.unauthorized_description" />},
  500: {title: <T keyName="c.Unknown_error" />, description: <T keyName="error_boundary.unknown_error_description" />},
};

const ErrorLayout = ({children}: PropsWithChildren) => (
  <Flex position="absolute" w="100vw" minH="100vh" top={0} left={0} alignItems="center" justifyContent="center">
    <Flex asChild>
      <main>{children}</main>
    </Flex>
  </Flex>
);

const BackendError = ({errorResponse: {status}, children}: PropsWithChildren<{errorResponse: ErrorResponse}>) => {
  const {title, description} = ERROR_MESSAGES[status] || ERROR_MESSAGES[500]!;

  return (
    <ErrorLayout>
      <Heading size="4xl" color="blue.500">
        {status}
      </Heading>
      <Box ml="6">
        <Box borderLeft="1px solid" borderColor="gray.200" pl="6">
          <Heading size="4xl" color="gray.800">
            {title}
          </Heading>
          {description && (
            <Text mt="2" color="gray.600">
              {description}
            </Text>
          )}
          {children}
        </Box>
        <ButtonGroup mt="10" pl="6" colorPalette="blue">
          <Button asChild>
            <Link href="/" _hover={{textDecoration: "none"}}>
              <T keyName="c.Go_home" />
            </Link>
          </Button>
          <Button asChild>
            <Link href="mailto:support@platformed.com" _hover={{textDecoration: "none"}}>
              <T keyName="c.Contact_support" />
            </Link>
          </Button>
        </ButtonGroup>
      </Box>
    </ErrorLayout>
  );
};
const FrontendUncaught = ({children}: PropsWithChildren) => (
  <ErrorLayout>
    <Heading size="4xl" color="orange.200">
      <Icon as={ExclamationTriangleIcon} boxSize="1em" />
    </Heading>
    <Box ml="6">
      <Box borderLeft="1px solid" borderColor="gray.200" pl="6">
        <Heading size="4xl" color="gray.800">
          {ERROR_MESSAGES[500]?.title}
        </Heading>
        <Text mt="2" color="gray.600">
          {ERROR_MESSAGES[500]?.description}
        </Text>
        {children}
      </Box>
      <ButtonGroup mt="10" pl="6" colorPalette="blue">
        <Button asChild>
          <Link href="/" _hover={{textDecoration: "none"}}>
            <T keyName="c.Go_home" />
          </Link>
        </Button>
        <Button asChild>
          <Link href="mailto:support@platformed.com" _hover={{textDecoration: "none"}}>
            <T keyName="c.Contact_support" />
          </Link>
        </Button>
      </ButtonGroup>
    </Box>
  </ErrorLayout>
);

const DetailedError = ({error, eventId}: {error?: HTTPError | ErrorResponse; eventId?: string}) => (
  <Box mt="6" maxW={600}>
    {(error as Partial<HTTPError>)?.response?.url && (
      <>
        <Text color="gray.500">
          <T keyName="c.Request_url[colon]" />
        </Text>
        <Text color="gray.800">{(error as HTTPError).response.url}</Text>
      </>
    )}
    {(error as Partial<HTTPError>)?.json && (
      <>
        <Text color="gray.500">
          <T keyName="c.Response_body[colon]" />
        </Text>
        <Text color="gray.800">{JSON.stringify((error as HTTPError).json)}</Text>
      </>
    )}
    {(error as Partial<ErrorResponse>)?.data && (
      <>
        <Text color="gray.500">
          <T keyName="c.Response_body[colon]" />
        </Text>
        <Text color="gray.800">{JSON.stringify((error as ErrorResponse).data)}</Text>
      </>
    )}
    {eventId && (
      <>
        <Text color="gray.500">
          <T keyName="c.Sentry_event_id[colon]" />
        </Text>
        <Text color="gray.800">{eventId}</Text>
      </>
    )}
  </Box>
);

const renderError: FallbackRender = ({error}) => {
  if (error instanceof RedirectError) return <Loading m="8" />;

  if (error instanceof HTTPError) return <BackendError errorResponse={error.response} />;

  if (isRouteErrorResponse(error)) return <BackendError errorResponse={error} />;

  return <FrontendUncaught />;
};

const ErrorRoot = ({children}: {children?: ReactNode}) => {
  const ref = useRef<ErrorBoundary | null>(null);
  const onError = useCallback(async (error: unknown) => {
    if (error instanceof RedirectError) {
      console.info(error.toString());
      await error.response.follow();
      ref.current?.resetErrorBoundary();
    }
  }, []);

  // Reset the error when the URL changes, so that the back button works when
  // encountering a 404.
  //
  // `subscribe` is marked internal only, but it's not like they gave us any other
  // options...
  useEffect(() => router!.subscribe(() => ref.current?.resetErrorBoundary()), []);

  return (
    <ErrorBoundary ref={ref} onError={onError} fallback={renderError}>
      {children}
    </ErrorBoundary>
  );
};

const ProductErrorBoundary = () => {
  const error = useRouteError();

  useEffect(() => {
    if (error instanceof RedirectError) {
      console.info(error.toString());
      error.response.follow();
    }
  }, [error]);

  if (error instanceof RedirectError)
    return (
      <>
        <Loading m="8" />
        <Navigate to={error.response.url} replace />
      </>
    );

  return (
    <ErrorLayout>
      {isRouteErrorResponse(error) && (
        <BackendError errorResponse={error}>
          <Internal>
            <DetailedError error={error} />
          </Internal>
        </BackendError>
      )}
      {error instanceof HTTPError && (
        <BackendError errorResponse={error.response}>
          <Internal>
            <DetailedError error={error} />
          </Internal>
        </BackendError>
      )}
    </ErrorLayout>
  );
};

export default ProductErrorBoundary;
export {ErrorRoot};
